import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  GuardrailComposition,
  InsertGuardrailCompositionBody,
  UpdateGuardrailCompositionNewImageBody,
  UpdateGuardrailCompositionRemoveImageBody,
} from "./types/guardrailCompositions";

const adminGuardrailCompositionsUrl = "/admin/settings/border/guardrail_compositions";

export const useGuardrailCompositionsApi = () => {
  const api = useApi();

  const getAdminGuardrailComposition = useCallback(
    (guardrailCompositionId: string, signal: AbortSignal) => {
      const url = `${adminGuardrailCompositionsUrl}/${guardrailCompositionId}`;
      return useSWRMutation<GuardrailComposition, CustomError, string>(url, (url) =>
        api.get<GuardrailComposition>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminGuardrailCompositions = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(adminGuardrailCompositionsUrl, query);
      return useSWRImmutable<Paginated<GuardrailComposition>, CustomError, string>(url, (url) =>
        api.get<Paginated<GuardrailComposition>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminGuardrailCompositions = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(adminGuardrailCompositionsUrl, query);
      return api.get<Paginated<GuardrailComposition>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminGuardrailComposition = useCallback(
    async (body: InsertGuardrailCompositionBody): Promise<{ id: string }> => {
      const id = await api.post<{ id: string }, InsertGuardrailCompositionBody>(adminGuardrailCompositionsUrl, body);
      mutateCache([adminGuardrailCompositionsUrl]);
      return id;
    },
    [api],
  );

  const updateAdminGuardrailComposition = useCallback(
    async (
      guardrailCompositionId: string,
      body: UpdateGuardrailCompositionRemoveImageBody | UpdateGuardrailCompositionNewImageBody,
    ): Promise<void> => {
      const url = `${adminGuardrailCompositionsUrl}/${guardrailCompositionId}`;
      await api.put<UpdateGuardrailCompositionRemoveImageBody | UpdateGuardrailCompositionNewImageBody>(url, body);
      mutateCache([adminGuardrailCompositionsUrl]);
    },
    [api],
  );

  const updateAdminGuardrailCompositionVisibility = useCallback(
    async (guardrailCompositionId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `${adminGuardrailCompositionsUrl}/${guardrailCompositionId}/visibility`;
      (await api.put<UpdateVisibilityRequestBody>(url, visibility), mutateCache([adminGuardrailCompositionsUrl]));
    },
    [api],
  );

  const updateAdminGuardrailCompositionOrder = useCallback(
    async (guardrailCompositionId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `${adminGuardrailCompositionsUrl}/${guardrailCompositionId}/order`;
      (await api.put<UpdateOrderRequestBody>(url, order), mutateCache([adminGuardrailCompositionsUrl]));
    },
    [api],
  );

  const deleteAdminGuardrailComposition = useCallback(
    async (guardrailCompositionId: string): Promise<void> => {
      const url = `${adminGuardrailCompositionsUrl}/${guardrailCompositionId}`;
      await api.del(url).then(() => {
        mutateCache([adminGuardrailCompositionsUrl]);
      });
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (guardrailCompositionId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `${adminGuardrailCompositionsUrl}/${guardrailCompositionId}/image/generate_upload_url?file_name=${encodedFileName}`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [api],
  );

  return {
    getAdminGuardrailComposition,
    useAdminGuardrailCompositions,
    getAdminGuardrailCompositions,
    insertAdminGuardrailComposition,
    updateAdminGuardrailComposition,
    updateAdminGuardrailCompositionVisibility,
    updateAdminGuardrailCompositionOrder,
    deleteAdminGuardrailComposition,
    generateUploadImageUrl,
  };
};
